import api from './api';
import axios from 'axios';
import { getToken } from '../utils/auth';
import { decodificaPayload, isRispostaCodificata } from '../utils/responseDecoder';

// Configurazione base per le API del network
const networkApi = axios.create({
  baseURL: '/api/network',
  timeout: 30000,
  headers: {
    'Content-Type': 'application/json'
  }
});

// Interceptor per aggiungere token di autenticazione
networkApi.interceptors.request.use(
  config => {
    const token = getToken();
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  error => Promise.reject(error)
);

// Flag per disabilitare logout automatico durante il login
let isLoggingIn = false;

// Funzione per impostare la flag di login
export const setNetworkLoggingIn = (value) => {
  isLoggingIn = value;
};

// Interceptor per gestire le risposte
networkApi.interceptors.response.use(
  response => {
    if (isRispostaCodificata(response.data)) {
      try {
        response.data = decodificaPayload(response.data._p);
      } catch (e) { /* fallback */ }
    }
    return response.data;
  },
  error => {
    if (error.response && isRispostaCodificata(error.response.data)) {
      try {
        error.response.data = decodificaPayload(error.response.data._p);
      } catch (e) { /* fallback */ }
    }
    console.error('Errore API Network:', error);
    
    if (error.response?.status === 401 && !isLoggingIn) {
      localStorage.removeItem('token');
      window.location.href = '/login';
    }
    
    return Promise.reject(error);
  }
);

// Servizi API per il modulo Network
const networkService = {
  // ============================================
  // SERVIZI SISTEMA E HEALTH CHECK
  // ============================================
  
  /**
   * Verifica lo stato di salute del sistema
   */
  getSystemHealth: async () => {
    return await networkApi.get('/health');
  },

  /**
   * Ottiene informazioni generali sul sistema network
   */
  getSystemInfo: async () => {
    return await networkApi.get('/');
  },

  // ============================================
  // SERVIZI GRUPPI
  // ============================================
  
  /**
   * Ottiene la lista dei gruppi con filtri e paginazione
   */
  getGruppi: async (params = {}) => {
    return await networkApi.get('/gruppi', { params });
  },

  /**
   * Ottiene i dettagli di un gruppo specifico
   */
  getGruppoDettagliato: async (gruppoId) => {
    return await networkApi.get(`/gruppi/${gruppoId}`);
  },

  /**
   * Crea un nuovo gruppo con la sua Agenzia Primaria
   */
  creaGruppo: async (datiGruppo) => {
    return await networkApi.post('/gruppi', datiGruppo);
  },

  /**
   * Aggiorna un gruppo esistente
   */
  aggiornaGruppo: async (gruppoId, datiAggiornamento) => {
    return await networkApi.put(`/gruppi/${gruppoId}`, datiAggiornamento);
  },

  /**
   * Disattiva un gruppo
   */
  disattivaGruppo: async (gruppoId) => {
    return await networkApi.delete(`/gruppi/${gruppoId}`);
  },

  /**
   * Ottiene la gerarchia completa di un gruppo
   */
  getGerarchiaGruppo: async (gruppoId) => {
    return await networkApi.get(`/gruppi/${gruppoId}/gerarchia`);
  },

  /**
   * Ottiene le statistiche di un gruppo
   */
  getStatisticheGruppo: async (gruppoId) => {
    return await networkApi.get(`/gruppi/${gruppoId}/statistiche`);
  },

  /**
   * Clona un gruppo esistente
   */
  clonaGruppo: async (gruppoId, nuoviDati) => {
    return await networkApi.post(`/gruppi/${gruppoId}/clona`, nuoviDati);
  },

  /**
   * Verifica se un utente può essere proprietario
   */
  verificaProprietario: async (userId) => {
    return await networkApi.post('/gruppi/verifica-proprietario', { userId });
  },

  // ============================================
  // SERVIZI NODI RETE
  // ============================================
  
  /**
   * Ottiene la lista dei nodi con filtri
   */
  getNodi: async (filtri = {}) => {
    const params = new URLSearchParams();
    if (filtri.gruppoId) params.append('gruppoId', filtri.gruppoId);
    if (filtri.parentId !== undefined) params.append('parentId', filtri.parentId);
    if (filtri.tipoNodo) params.append('tipoNodo', filtri.tipoNodo);
    if (filtri.attivo !== undefined) params.append('attivo', filtri.attivo);
    if (filtri.livello !== undefined) params.append('livello', filtri.livello);
    
    const queryString = params.toString();
    return await networkApi.get(`/nodi${queryString ? '?' + queryString : ''}`);
  },

  /**
   * Ottiene i dettagli di un nodo specifico
   */
  getNodoDettagliato: async (nodoId) => {
    return await networkApi.get(`/nodi/${nodoId}`);
  },

  /**
   * Crea un nuovo nodo nella rete
   */
  creaNodo: async (datiNodo) => {
    return await networkApi.post('/nodi', datiNodo);
  },

  /**
   * Crea un nuovo utente e nodo insieme
   */
  creaNodoConUtente: async (datiUtente, datiNodo) => {
    return await networkApi.post('/nodi/crea-con-utente', { datiUtente, datiNodo });
  },

  /**
   * Aggiorna un nodo esistente
   */
  aggiornaNodo: async (nodoId, datiAggiornamento) => {
    return await networkApi.put(`/nodi/${nodoId}`, datiAggiornamento);
  },

  /**
   * Disattiva un nodo
   */
  disattivaNodo: async (nodoId, opzioni) => {
    return await networkApi.delete(`/nodi/${nodoId}`, { data: opzioni });
  },

  /**
   * Sposta un nodo sotto un nuovo padre
   */
  spostaNodo: async (nodoId, nuovoParentId, gestioneFigli = 'porta_ramo') => {
    return await networkApi.put(`/nodi/${nodoId}/sposta`, { nuovoParentId, gestioneFigli });
  },

  /**
   * Ottiene i discendenti di un nodo
   */
  getDiscendenti: async (nodoId) => {
    return await networkApi.get(`/nodi/${nodoId}/discendenti`);
  },

  /**
   * Ottiene gli antenati di un nodo
   */
  getAntenati: async (nodoId) => {
    return await networkApi.get(`/nodi/${nodoId}/antenati`);
  },

  // ============================================
  // SERVIZI PRODUZIONI
  // ============================================
  
  /**
   * Ottiene la lista delle produzioni con filtri
   */
  getProduzioni: async (params = {}) => {
    return await networkApi.get('/produzioni', { params });
  },

  /**
   * Ottiene i dettagli di una produzione
   */
  getProduzioneDettagliata: async (produzioneId) => {
    return await networkApi.get(`/produzioni/${produzioneId}`);
  },

  /**
   * Crea una nuova produzione
   */
  creaProduzione: async (datiProduzione) => {
    return await networkApi.post('/produzioni', datiProduzione);
  },

  /**
   * Aggiorna una produzione esistente
   */
  aggiornaProduzione: async (produzioneId, datiAggiornamento) => {
    return await networkApi.put(`/produzioni/${produzioneId}`, datiAggiornamento);
  },

  /**
   * Approva una produzione
   */
  approvaProduzione: async (produzioneId) => {
    return await networkApi.post(`/produzioni/${produzioneId}/approva`);
  },

  /**
   * Annulla una produzione
   */
  annullaProduzione: async (produzioneId, motivo) => {
    return await networkApi.post(`/produzioni/${produzioneId}/annulla`, { motivo });
  },

  /**
   * Ottiene le statistiche delle produzioni
   */
  getStatisticheProduzioni: async (filtri = {}) => {
    return await networkApi.get('/produzioni/statistiche', { params: filtri });
  },

  /**
   * Esporta le produzioni in CSV
   */
  esportaProduzioniCSV: async (filtri = {}) => {
    return await networkApi.get('/produzioni/export/csv', { 
      params: filtri,
      responseType: 'blob'
    });
  },

  // ============================================
  // SERVIZI CALCOLO PROVVIGIONI
  // ============================================
  
  /**
   * Calcola le provvigioni per una produzione
   */
  calcolaProvvigioni: async (produzioneId, opzioni = {}) => {
    return await networkApi.post(`/calcolo/${produzioneId}/calcola`, opzioni);
  },

  /**
   * Verifica le percentuali di un percorso
   */
  verificaPercentuali: async (nodoGeneranteId, importoTest = 1000) => {
    return await networkApi.post('/calcolo/verifica-percentuali', {
      nodoGeneranteId,
      importoTest
    });
  },

  /**
   * Ottiene lo storico delle distribuzioni
   */
  getStoricoDistribuzioni: async (params = {}) => {
    return await networkApi.get('/calcolo/distribuzioni', { params });
  },

  /**
   * Approva una distribuzione
   */
  approvaDistribuzione: async (distribuzioneId) => {
    return await networkApi.post(`/calcolo/distribuzioni/${distribuzioneId}/approva`);
  },

  /**
   * Ottiene le statistiche delle provvigioni
   */
  getStatisticheProvvigioni: async (filtri = {}) => {
    return await networkApi.get('/calcolo/statistiche', { params: filtri });
  },

  // ============================================
  // SERVIZI SIMULATORE
  // ============================================
  
  /**
   * Simula il calcolo delle provvigioni
   */
  simulaCalcoloProvvigioni: async (nodoGeneranteId, importoSimulazione, opzioni = {}) => {
    return await networkApi.post('/simulatore/simula', {
      nodoGeneranteId,
      importoSimulazione,
      ...opzioni
    });
  },

  /**
   * Simula multipli scenari
   */
  simulaMultipliScenari: async (nodoGeneranteId, importiTest, opzioni = {}) => {
    return await networkApi.post('/simulatore/multi-scenari', {
      nodoGeneranteId,
      importiTest,
      ...opzioni
    });
  },

  /**
   * Simula modifica delle provvigioni
   */
  simulaModificaProvvigioni: async (nodoId, nuoveImpostazioni, importoTest, opzioni = {}) => {
    return await networkApi.post('/simulatore/modifica-provvigioni', {
      nodoId,
      nuoveImpostazioni,
      importoTest,
      ...opzioni
    });
  },

  /**
   * Ottiene configurazioni predefinite per il simulatore
   */
  getConfigurazioniSimulatore: async () => {
    return await networkApi.get('/simulatore/configurazioni');
  },

  // ============================================
  // SERVIZI STATISTICHE
  // ============================================
  
  /**
   * Ottiene le statistiche generali della rete
   */
  getStatisticheGenerali: async (filtri = {}) => {
    return await networkApi.get('/statistiche/generali', { params: filtri });
  },

  /**
   * Ottiene le statistiche di performance
   */
  getStatistichePerformance: async (filtri = {}) => {
    return await networkApi.get('/statistiche/performance', { params: filtri });
  },

  /**
   * Ottiene le statistiche di trending
   */
  getStatisticheTrending: async (periodo = '30d') => {
    return await networkApi.get('/statistiche/trending', { params: { periodo } });
  },

  /**
   * Ottiene analisi avanzate
   */
  getAnalisiAvanzate: async (tipoAnalisi, parametri = {}) => {
    return await networkApi.post('/statistiche/analisi-avanzate', {
      tipoAnalisi,
      parametri
    });
  },

  /**
   * Esporta statistiche in formato Excel
   */
  esportaStatisticheExcel: async (configurazione) => {
    return await networkApi.post('/statistiche/export/excel', configurazione, {
      responseType: 'blob'
    });
  },

  /**
   * Ottiene previsioni e forecasting
   */
  getPrevisioni: async (modello = 'lineare', parametri = {}) => {
    return await networkApi.post('/statistiche/previsioni', {
      modello,
      parametri
    });
  },

  // ============================================
  // SERVIZI AUDIT TRAIL
  // ============================================
  
  /**
   * Ottiene lo storico delle operazioni
   */
  getStoricoOperazioni: async (params = {}) => {
    return await networkApi.get('/audit', { params });
  },

  /**
   * Ottiene le statistiche delle operazioni audit
   */
  getStatisticheAudit: async (filtri = {}) => {
    return await networkApi.get('/audit/statistiche', { params: filtri });
  },

  /**
   * Esporta il log di audit in CSV
   */
  esportaAuditCSV: async (filtri = {}) => {
    return await networkApi.get('/audit/export/csv', { 
      params: filtri,
      responseType: 'blob'
    });
  },

  /**
   * Esporta il log di audit in JSON
   */
  esportaAuditJSON: async (filtri = {}) => {
    return await networkApi.get('/audit/export/json', { 
      params: filtri,
      responseType: 'blob'
    });
  },

  /**
   * Pulisce lo storico audit (admin only)
   */
  pulisciStoricoAudit: async (giorniMantenimento = 365) => {
    return await networkApi.post('/audit/pulisci', { giorniMantenimento });
  },

  // ============================================
  // SERVIZI CONTROLLO INTEGRITÀ
  // ============================================
  
  /**
   * Esegue un controllo completo dell'integrità
   */
  verificaIntegritaCompleta: async () => {
    return await networkApi.post('/controllo-integrita/verifica-completa');
  },

  /**
   * Verifica l'integrità di un componente specifico
   */
  verificaComponente: async (componente) => {
    return await networkApi.post('/controllo-integrita/verifica-componente', {
      componente
    });
  },

  /**
   * Ottiene il report di integrità
   */
  getReportIntegrita: async () => {
    return await networkApi.get('/controllo-integrita/report');
  },

  /**
   * Esegue riparazioni automatiche
   */
  eseguiRiparazioniAutomatiche: async () => {
    return await networkApi.post('/controllo-integrita/ripara-automatico');
  },

  /**
   * Ottiene lo storico dei controlli
   */
  getStoricoControlli: async (params = {}) => {
    return await networkApi.get('/controllo-integrita/storico', { params });
  },

  /**
   * Verifica un percorso specifico
   */
  verificaPercorsoSpecifico: async (nodoGeneranteId, importoTest = 1000) => {
    return await networkApi.post('/controllo-integrita/verifica-percorso', {
      nodoGeneranteId,
      importoTest
    });
  },

  // ============================================
  // SERVIZI GESTIONE UTENTI
  // ============================================
  
  /**
   * Ottiene la lista degli utenti rete (agenzie e subagenti)
   */
  getUtentiRete: async (params = {}) => {
    return await networkApi.get('/utenti/rete', { params });
  },

  /**
   * Ottiene i dettagli di un utente rete
   */
  getUtenteRete: async (utenteId) => {
    return await networkApi.get(`/utenti/rete/${utenteId}`);
  },

  /**
   * Crea un nuovo utente rete
   */
  creaUtenteRete: async (datiUtente) => {
    return await networkApi.post('/utenti/rete', datiUtente);
  },

  /**
   * Aggiorna un utente rete
   */
  aggiornaUtenteRete: async (utenteId, datiAggiornamento) => {
    return await networkApi.put(`/utenti/rete/${utenteId}`, datiAggiornamento);
  },

  /**
   * Elimina/disattiva un utente rete
   */
  eliminaUtenteRete: async (utenteId, eliminaDefinitivamente = false) => {
    return await networkApi.delete(`/utenti/rete/${utenteId}`, { 
      data: { eliminaDefinitivamente } 
    });
  },

  /**
   * Reset password utente rete
   */
  resetPasswordUtenteRete: async (utenteId, nuovaPassword) => {
    return await networkApi.post(`/utenti/rete/${utenteId}/reset-password`, { 
      nuovaPassword 
    });
  },

  /**
   * Ottiene la lista degli admin
   */
  getAdmin: async (params = {}) => {
    return await networkApi.get('/utenti/admin', { params });
  },

  /**
   * Ottiene i dettagli di un admin
   */
  getAdminDettagli: async (adminId) => {
    return await networkApi.get(`/utenti/admin/${adminId}`);
  },

  /**
   * Crea un nuovo admin
   */
  creaAdmin: async (datiAdmin) => {
    return await networkApi.post('/utenti/admin', datiAdmin);
  },

  /**
   * Aggiorna un admin
   */
  aggiornaAdmin: async (adminId, datiAggiornamento) => {
    return await networkApi.put(`/utenti/admin/${adminId}`, datiAggiornamento);
  },

  /**
   * Elimina/disattiva un admin
   */
  eliminaAdmin: async (adminId, eliminaDefinitivamente = false) => {
    return await networkApi.delete(`/utenti/admin/${adminId}`, { 
      data: { eliminaDefinitivamente } 
    });
  },

  /**
   * Reset password admin
   */
  resetPasswordAdmin: async (adminId, nuovaPassword) => {
    return await networkApi.post(`/utenti/admin/${adminId}/reset-password`, { 
      nuovaPassword 
    });
  },

  /**
   * Ottiene statistiche utenti
   */
  getStatisticheUtenti: async () => {
    return await networkApi.get('/utenti/statistiche');
  },

  /**
   * Ottiene i ruoli disponibili
   */
  getRuoliDisponibili: async () => {
    return await networkApi.get('/utenti/ruoli');
  },

  // ============================================
  // SERVIZI RETE CORRENTE
  // ============================================
  
  /**
   * Ottiene la rete corrente
   */
  getReteCorrente: async () => {
    return await networkApi.get('/rete-corrente');
  },

  /**
   * Crea un nuovo nodo nella rete (POST)
   */
  postNodi: async (datiNodo) => {
    return await networkApi.post('/nodi', datiNodo);
  },

  /**
   * Elimina un nodo dalla rete (DELETE)
   */
  deleteNodi: async (nodoId) => {
    return await networkApi.delete(`/nodi/${nodoId}`);
  }
};

// Log per debug
//console.log('Network API service caricato e configurato');

export default networkService; 